/**
 * FIT A PICKED IMAGE INTO AN UPLOAD LIMIT — by resizing it, not by refusing it.
 *
 * Every camera on every phone in the field produces a 4–12 MB photo. A notification picture may be
 * 2 MB (FCM will not fetch a bigger one), so the composer's file picker used to end in
 * "Image too large — the limit is 2 MB": a dead end that told the operator the number they had
 * already failed to meet and left them to find image-editing software. The browser is perfectly
 * capable of doing the resize itself, which is what this does.
 *
 * The long edge is capped first — a 4000 px photo carries detail no notification tray will ever
 * show — and the JPEG quality is then stepped down only as far as the limit actually requires, so a
 * picture that fits at 0.9 is never re-encoded at 0.6. A file that is already small enough and is a
 * format the server accepts is passed through UNTOUCHED: re-encoding a crisp 300 KB PNG into a
 * softer JPEG for no reason is a loss, not a service.
 *
 * Returns the file to upload plus what happened, so the caller can say so out loud — silently
 * uploading something other than the file the person picked is its own kind of surprise.
 */

export interface FittedImage {
  file: File;
  /** True when the picture was resized and/or re-encoded to get under the limit. */
  changed: boolean;
  originalBytes: number;
  bytes: number;
  width: number;
  height: number;
}

const DEFAULT_MAX_EDGE = 1440; // plenty for a notification's big-picture area on any handset
const QUALITY_LADDER = [0.92, 0.85, 0.78, 0.7, 0.6, 0.5, 0.4];

/** Decode a File into an <img>, with the object URL released whatever happens. */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      // A HEIC/HEIF straight off an iPhone lands here: the browser cannot decode it, so neither
      // this nor the server can do anything with it, and saying which format to use is the only
      // useful answer.
      reject(new Error('That file could not be read as an image — save it as JPG, PNG or WebP and try again.'));
    };
    img.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not re-encode the image'))), type, quality));
}

/** `photo.heic` → `photo.jpg`; the stored name keeps the name the file arrived with otherwise. */
function renameTo(name: string, ext: string): string {
  const base = name.replace(/\.[^.]+$/, '') || 'picture';
  return `${base}.${ext}`;
}

export async function fitImageForUpload(
  file: File,
  opts: { maxBytes: number; maxEdge?: number } = { maxBytes: 2 * 1024 * 1024 },
): Promise<FittedImage> {
  const maxBytes = opts.maxBytes;
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;
  const serverAccepts = /^image\/(png|jpeg|webp)$/.test(file.type);

  const img = await loadImage(file);
  const { naturalWidth: w0, naturalHeight: h0 } = img;

  // Already acceptable: right format, under the limit, and not absurdly large. Leave it alone.
  if (serverAccepts && file.size <= maxBytes && Math.max(w0, h0) <= maxEdge * 2) {
    URL.revokeObjectURL(img.src);
    return { file, changed: false, originalBytes: file.size, bytes: file.size, width: w0, height: h0 };
  }

  const scale = Math.min(1, maxEdge / Math.max(w0, h0));
  let w = Math.max(1, Math.round(w0 * scale));
  let h = Math.max(1, Math.round(h0 * scale));

  const canvas = document.createElement('canvas');
  const draw = () => {
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser cannot resize images — pick a smaller picture.');
    // A white bed under the drawing: a transparent PNG re-encoded as JPEG would otherwise come out
    // with black where the transparency was.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
  };

  try {
    draw();
    for (const quality of QUALITY_LADDER) {
      const blob = await toBlob(canvas, 'image/jpeg', quality);
      if (blob.size <= maxBytes) {
        return {
          file: new File([blob], renameTo(file.name, 'jpg'), { type: 'image/jpeg' }),
          changed: true, originalBytes: file.size, bytes: blob.size, width: w, height: h,
        };
      }
    }
    // Still too big at the lowest quality this will go to — halve the edge and try the ladder once
    // more rather than handing back a picture nobody can read.
    w = Math.max(1, Math.round(w / 2));
    h = Math.max(1, Math.round(h / 2));
    draw();
    for (const quality of QUALITY_LADDER) {
      const blob = await toBlob(canvas, 'image/jpeg', quality);
      if (blob.size <= maxBytes) {
        return {
          file: new File([blob], renameTo(file.name, 'jpg'), { type: 'image/jpeg' }),
          changed: true, originalBytes: file.size, bytes: blob.size, width: w, height: h,
        };
      }
    }
    throw new Error('This picture could not be brought under the size limit — try a simpler image.');
  } finally {
    URL.revokeObjectURL(img.src);
  }
}

/** "8.6 MB" / "412 KB" — for saying what a picture weighed before and after. */
export function formatBytes(n: number): string {
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}
